import { useCallback, useEffect, useRef } from 'react';

import { useFocusEffect } from 'expo-router';
import fastDeepEqual from 'fast-deep-equal';
import { useLoyaltyUserQuery } from 'features/loyalty-user/use-loyalty-user-query';

import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { updateLaunchDarklyUser } from 'state/launchdarkly/launchdarkly-core';

import type { IUseLoyaltyUserState } from './types';
import { useIsLoyaltyEnabled } from './use-is-loyalty-enabled';
import { useLoyaltyUserId } from './use-loyalty-user';

/**
  Get loyalty user state (Rewards Points etc.) fresh or from redux. will always populate redux with latest.
  ⚠️ Just need loyaltyId? use useLoyaltyUserId hook ⚠️
 */
export const useLoyaltyUserState = ({
  refreshOnMountOrFocus,
  onSuccess,
}: {
  /** ensure loyalty info isn't stale */
  refreshOnMountOrFocus: boolean;
  onSuccess?: Function;
}): IUseLoyaltyUserState => {
  const dispatch = useAppDispatch();
  const { loyaltyUser: cacheLoyaltyUser, loading: cacheIsLoading } = useAppSelector(
    selectors.loyalty.selectLoyaltyUser
  );
  const { loyaltyUserId: loyaltyId } = useLoyaltyUserId();
  const loyaltyEnabled = useIsLoyaltyEnabled();
  const loyaltyIdentityRef = useRef(loyaltyId);
  const cacheLoyaltyUserRef = useRef(cacheLoyaltyUser);
  const refreshRequestId = useRef(0);
  loyaltyIdentityRef.current = loyaltyId;
  cacheLoyaltyUserRef.current = cacheLoyaltyUser;

  // TODO: move this to offer's context
  const offersCooldownEnabled = Boolean(useFlag(LaunchDarklyFlag.ENABLE_GLOBAL_OFFERS_COOLDOWN));

  const {
    called,
    error,
    fetchLoyaltyUser,
    loading: localLoading,
    loyaltyUser: queriedLoyaltyUser,
    source,
  } = useLoyaltyUserQuery();
  const previousSourceRef = useRef(source);
  const sourceChanged = previousSourceRef.current !== source;

  // Mimic useEvent for callback passed as props
  // Refer to: https://github.com/reactjs/rfcs/blob/useevent/text/0000-useevent.md
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  // Load if loyalty is enabled and a loyaltyId exists. If refreshOnMountOrFocus is true, always fetch fresh data; otherwise, fetch only if there is no cached loyalty user and the query hasn't been called yet.
  const cacheMatchesIdentity = Boolean(loyaltyId && cacheLoyaltyUser?.id === loyaltyId);
  // Whether the current identity needs a fetch at all, independent of the `sourceChanged` timing
  // below. `shouldLoad` (the actual trigger for the mount/focus effect) further gates this on
  // `!sourceChanged`, since a source change is handled by its own effect instead. `loading` also
  // derives from this same expression so the two can't drift apart (see `loading` below).
  const needsFetchForIdentity = Boolean(
    loyaltyEnabled && loyaltyId && (refreshOnMountOrFocus || (!cacheMatchesIdentity && !called))
  );
  const shouldLoad = needsFetchForIdentity && !sourceChanged;

  const refresh: IUseLoyaltyUserState['refetch'] = useCallback(
    async variables => {
      const effectiveLoyaltyId = variables?.loyaltyId ?? loyaltyIdentityRef.current;
      if (!effectiveLoyaltyId) {
        return undefined;
      }

      const requestId = refreshRequestId.current + 1;
      refreshRequestId.current = requestId;
      dispatch(actions.loyalty.setLoyaltyLoading(true));
      try {
        const loyaltyUser = await fetchLoyaltyUser({ loyaltyId: effectiveLoyaltyId });
        if (
          loyaltyUser === undefined ||
          effectiveLoyaltyId !== loyaltyIdentityRef.current ||
          (loyaltyUser !== null && loyaltyUser.id !== loyaltyIdentityRef.current)
        ) {
          return undefined;
        }

        if (!fastDeepEqual(loyaltyUser, cacheLoyaltyUserRef.current)) {
          dispatch(actions.loyalty.setUser(loyaltyUser));
        }

        const offerRedemptionAvailableAfter =
          loyaltyUser?.offerRedemptionAvailability?.availableAfter;
        if (offerRedemptionAvailableAfter && offersCooldownEnabled) {
          dispatch(actions.loyalty.setOfferRedemptionAvailableAfter(offerRedemptionAvailableAfter));
        }

        updateLaunchDarklyUser(
          {
            loyaltyId: loyaltyUser?.id,
          },
          'loyalty user id change'
        );

        if (onSuccessRef.current) {
          onSuccessRef.current();
        }
        return loyaltyUser;
      } finally {
        if (refreshRequestId.current === requestId) {
          dispatch(actions.loyalty.setLoyaltyLoading(false));
        }
      }
    },
    [dispatch, fetchLoyaltyUser, offersCooldownEnabled]
  );

  useEffect(() => {
    if (!sourceChanged || !loyaltyEnabled || !loyaltyId) {
      return;
    }

    previousSourceRef.current = source;
    void refresh().catch(() => undefined);
  }, [loyaltyEnabled, loyaltyId, refresh, source, sourceChanged]);

  const loadLoyaltyUser = useCallback(() => {
    if (shouldLoad) {
      void refresh().catch(() => undefined);
    }
  }, [shouldLoad, refresh]);

  // for on mount
  useEffect(() => {
    loadLoyaltyUser();
  }, [loadLoyaltyUser]);

  // for when expo router refocuses the already loaded screen
  useFocusEffect(loadLoyaltyUser);

  const loyaltyUser =
    called && queriedLoyaltyUser === null
      ? null
      : queriedLoyaltyUser?.id === loyaltyId
      ? queriedLoyaltyUser
      : cacheMatchesIdentity
      ? cacheLoyaltyUser
      : null;

  // `localLoading`/`called` reset to `false` for one render right when `source` changes (see
  // `stateMatchesSelection` in useLoyaltyUserQuery), before the source-changed effect above has
  // had a chance to kick off the refetch. Without the `needsFetchForIdentity && !called` term,
  // that render would read `loading: false` with a stale cached `loyaltyUser` still in hand -
  // briefly showing outdated data before flipping back to loading and then to the fresh result.
  const loading = localLoading || cacheIsLoading || (needsFetchForIdentity && !called);

  return {
    loyaltyUser,
    error,
    refetch: refresh,
    loading,
  };
};
